import {Component, EventEmitter, Input, Output} from '@angular/core';
import {ArticleRatingService} from "@iw-shop/article/services/article-rating.service";
import {ApiArticleRating, ApiArticleRatingsSetRequest} from "@api/article-ratings";
import {FormBuilder, FormGroup} from "@angular/forms";
import {FormStatus} from "@iw-shop/forms/components/submit-button/submit-button.component";
import {AppState} from "@store/state/app.state";
import {Store} from "@ngrx/store";
import {getSelectedCustomer} from "@store/selectors/user.selector";
import {take} from "rxjs/operators";
import {Observable} from "rxjs";
import {Customer} from "@iw-shop/customer/models/customer";
import {faStar as faStarSolid} from "@fortawesome/free-solid-svg-icons";
import {faStar as faStarRegular} from "@fortawesome/free-regular-svg-icons";

@Component({
    selector: 'iw-shop-article-rating',
    standalone: false,
    templateUrl: './article-rating.component.html',
    styleUrls: ['./article-rating.component.scss']
})
export class ArticleRatingComponent {

    @Input() articleId!: string;
    @Output() getAverageRating = new EventEmitter<number>();
    @Output() getCountTotal = new EventEmitter<number>();

    averageArticleRating: number = 0;
    articleRatings: ApiArticleRating[] = [];
    articleRatingsTotal: number = 0;

    showModalRating: boolean = false;
    disableRating: boolean = false;

    form: FormGroup;

    formStatus: FormStatus = null;

    apiError?: {
        notification: string,
        errors?: { key: string, error: string }[]
    };

    customer$: Observable<Customer | undefined>;


    constructor(protected fb: FormBuilder, private articleRatingService: ArticleRatingService, private store: Store<AppState>) {
        this.form= this.fb.group({
            rating: [''],
            username: [''],
            comment: ['']
        });
        this.customer$ = this.store.select(getSelectedCustomer);
    }

    ngOnInit() {
        this.loadArticleRatings(this.articleId);
    }

    isFormRequestError(value: string): boolean {
        let form = this.form?.get(value);
        return (
            (
                form?.errors ||
                this.isFormRequestApiError(value)
            ) &&
            this.formStatus == 'submitForm');
    }

    isFormRequestApiError(key: string): boolean {
        return !!this.apiError?.errors?.filter(r => r.key == key)[0];
    }

    loadArticleRatings(articleId: string) {
        this.articleRatingService.get({
            filters: [{fields: ['articleId'], values: [articleId]}],
            articleRatings: {start: 0, limit: 10},
            averageArticleRating: {}
        })
            .subscribe(
                r => {
                    if (r.result) {
                        if (r.result.averageArticleRating) {
                            this.averageArticleRating = r.result.averageArticleRating;
                            this.getAverageRating.emit(this.averageArticleRating);
                        }
                        if (r.result.articleRatings.data[0]) {
                            this.articleRatingsTotal = r.result.articleRatings.count.filtered;
                            this.getCountTotal.emit(r.result.articleRatings.count.filtered);
                            this.articleRatings = r.result.articleRatings.data;
                            let userId = '';
                            this.store.select(getSelectedCustomer)
                                .pipe(
                                    take(1)
                                )
                                .subscribe(
                                    r => {
                                        if (r) {
                                            userId = r.id;
                                        }
                                    }
                                );
                            this.articleRatings.map(r => {
                                if (r.userId == userId) {
                                    this.disableRating = true;
                                }
                            });
                        }
                    }
                }
            );
    }

    submit() {
        this.formStatus = 'submitForm';
        this.apiError = undefined;

        if (this.form.valid) {
            this.formStatus = 'loading';
            let userId = '';
            this.store.select(getSelectedCustomer)
                .pipe(
                    take(1)
                )
                .subscribe(
                    r => {
                        if (r) {
                            userId = r.id;
                        }
                    }
                );

            let data: ApiArticleRatingsSetRequest['params']['data'] = {
                articleId: this.articleId,
                userId: userId,
                username: this.form.get('username')?.value,
                rating: parseInt(this.form.get('rating')?.value),
                comment: this.form.get('comment')?.value
            };

            this.articleRatingService.set(data)
                .subscribe(
                    {
                        next: (r) => {
                            this.formStatus = 'submitForm';
                            if (r.error) {
                                this.apiError = {notification: r.error.message};
                                if (r.error.input) {
                                    this.apiError.errors = r.error.input;
                                }
                                window.scroll({
                                    top: 0,
                                    left: 0,
                                    behavior: 'smooth'
                                });
                            } else {
                                this.formStatus = 'success';
                                this.loadArticleRatings(this.articleId);
                            }
                        },
                        error: () => {
                            this.formStatus = 'submitForm';
                        }
                    });
        }
    }

    protected readonly faStarSolid = faStarSolid;
    protected readonly faStarRegular = faStarRegular;
}
